import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useLocation } from 'wouter';
import { endpoints } from '@/lib/api';
import { Search as SearchIcon, ArrowRight, Server, Box } from 'lucide-react';
import { cn } from '@/lib/utils';

type Hit =
  | { kind: 'service'; id: string; label: string; hint: string; href: string }
  | { kind: 'lxc'; id: string; label: string; hint: string; href: string }
  | { kind: 'vm'; id: string; label: string; hint: string; href: string };

const REFETCH = 30_000;

export function GlobalSearch() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [sel, setSel] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const [, navigate] = useLocation();

  // '/' shortcut to focus
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === '/' && document.activeElement?.tagName !== 'INPUT' && document.activeElement?.tagName !== 'TEXTAREA') {
        e.preventDefault();
        setOpen(true);
        inputRef.current?.focus();
      }
      if (e.key === 'Escape') {
        setOpen(false);
        inputRef.current?.blur();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  // Fetch indexes when search is open
  const servicesQ = useQuery({
    queryKey: ['services', 'list'],
    queryFn: endpoints.services.list,
    enabled: open,
    refetchInterval: REFETCH,
  });
  const proxmoxQ = useQuery({
    queryKey: ['proxmox', 'lxc', 'qemu', 'index'],
    queryFn: async () => {
      const [lxc, qemu] = await Promise.all([endpoints.proxmox.lxc(), endpoints.proxmox.qemu()]);
      return { lxc: lxc.containers, qemu: qemu.vms };
    },
    enabled: open,
    refetchInterval: REFETCH,
  });

  const hits = filterHits(q, servicesQ.data?.services, proxmoxQ.data);

  // Reset selection when results change
  useEffect(() => {
    setSel(0);
  }, [q]);

  const submit = (h: Hit | undefined) => {
    if (!h) return;
    navigate(h.href);
    setOpen(false);
    setQ('');
  };

  return (
    <>
      <button
        onClick={() => {
          setOpen(true);
          setTimeout(() => inputRef.current?.focus(), 10);
        }}
        className="flex items-center gap-2 px-2.5 py-1 text-xs text-muted-foreground hover:text-foreground rounded-md border border-border bg-background/40 hover:bg-accent/40 transition-colors min-w-[200px]"
        title="Search (press /)"
      >
        <SearchIcon className="h-3.5 w-3.5" />
        <span className="flex-1 text-left">Search services & LXCs</span>
        <kbd className="font-mono text-[10px] text-muted-foreground/60 border border-border rounded px-1">/</kbd>
      </button>

      {open && (
        <div
          className="fixed inset-0 z-50 flex items-start justify-center pt-24 bg-black/60"
          onClick={() => setOpen(false)}
        >
          <div
            className="w-full max-w-xl rounded-lg border border-border bg-card shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-2 px-3 py-2 border-b border-border">
              <SearchIcon className="h-4 w-4 text-muted-foreground" />
              <input
                ref={inputRef}
                type="search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'ArrowDown') {
                    e.preventDefault();
                    setSel((s) => Math.min(s + 1, hits.length - 1));
                  } else if (e.key === 'ArrowUp') {
                    e.preventDefault();
                    setSel((s) => Math.max(s - 1, 0));
                  } else if (e.key === 'Enter') {
                    e.preventDefault();
                    submit(hits[sel]);
                  }
                }}
                placeholder="Type a service, LXC, or VM name…"
                className="flex-1 bg-transparent focus:outline-none text-sm"
              />
              <kbd className="font-mono text-[10px] text-muted-foreground/60 border border-border rounded px-1">
                esc
              </kbd>
            </div>
            <div className="max-h-96 overflow-y-auto">
              {hits.length === 0 ? (
                <div className="p-6 text-center text-sm text-muted-foreground">
                  {q ? 'no matches' : 'start typing to search…'}
                </div>
              ) : (
                <ul>
                  {hits.map((h, i) => {
                    const Icon = h.kind === 'lxc' ? Server : Box;
                    return (
                      <li key={`${h.kind}-${h.id}`}>
                        <button
                          onClick={() => submit(h)}
                          onMouseEnter={() => setSel(i)}
                          className={cn(
                            'w-full px-3 py-2 flex items-center gap-2.5 text-left text-sm transition-colors',
                            i === sel ? 'bg-accent' : 'hover:bg-accent/50',
                          )}
                        >
                          <Icon className="h-3.5 w-3.5 text-muted-foreground" />
                          <span className="flex-1 truncate">{h.label}</span>
                          <span className="text-xs text-muted-foreground">{h.hint}</span>
                          <ArrowRight className="h-3 w-3 text-muted-foreground" />
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function filterHits(
  query: string,
  services: Awaited<ReturnType<typeof endpoints.services.list>>['services'] | undefined,
  proxmox:
    | {
        lxc: Awaited<ReturnType<typeof endpoints.proxmox.lxc>>['containers'];
        qemu: Awaited<ReturnType<typeof endpoints.proxmox.qemu>>['vms'];
      }
    | undefined,
): Hit[] {
  const q = query.toLowerCase().trim();
  if (!q) {
    // Show 6 most recent services when empty
    return (services ?? [])
      .slice(0, 6)
      .map((s) => ({
        kind: 'service' as const,
        id: s.id,
        label: s.displayName,
        hint: `service · ${s.group}`,
        href: `/services/${s.id}`,
      }));
  }

  const hits: Hit[] = [];
  for (const s of services ?? []) {
    if (
      s.displayName.toLowerCase().includes(q) ||
      s.id.toLowerCase().includes(q) ||
      s.ip.includes(q) ||
      String(s.vmid).includes(q)
    ) {
      hits.push({
        kind: 'service',
        id: s.id,
        label: s.displayName,
        hint: `service · ${s.ip} · vmid ${s.vmid}`,
        href: `/services/${s.id}`,
      });
    }
  }
  for (const l of proxmox?.lxc ?? []) {
    if (l.name.toLowerCase().includes(q) || String(l.vmid).includes(q)) {
      hits.push({
        kind: 'lxc',
        id: String(l.vmid),
        label: `LXC ${l.vmid} — ${l.name}`,
        hint: l.status,
        href: `/proxmox/lxc/${l.vmid}`,
      });
    }
  }
  for (const v of proxmox?.qemu ?? []) {
    if (v.name.toLowerCase().includes(q) || String(v.vmid).includes(q)) {
      hits.push({
        kind: 'vm',
        id: String(v.vmid),
        label: `VM ${v.vmid} — ${v.name}`,
        hint: v.status,
        href: `/proxmox/lxc/${v.vmid}`, // reuse LXC detail for now
      });
    }
  }
  return hits.slice(0, 30);
}
